import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Users,
  MapPin,
  Calendar,
  Mail,
  Phone,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  GraduationCap,
  BookOpen,
} from 'lucide-react';

interface InstitutionDetails {
  institution: {
    id: string;
    name: string;
    slug: string;
    subdomain: string;
    status: string;
    subscriptionPlan: string | null;
    subscriptionEnds: string | null;
    email: string | null;
    phone: string | null;
    city: string | null;
    createdAt: string;
    updatedAt: string;
  };
  userStats: {
    total: number;
    byRole: Array<{
      role: string;
      label: string;
      count: number;
    }>;
  };
  campuses: Array<{
    id: string;
    name: string;
    city: string | null;
    isActive: boolean;
    userCount: number;
  }>;
  academicYears: Array<{
    id: string;
    name: string;
    isCurrent: boolean;
    startDate: string;
    endDate: string;
  }>;
  dgInfo: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    lastLoginAt: string | null;
    emailVerified: boolean;
    status: string;
  } | null;
}

interface ViewInstitutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  institutionId: string | null;
}

const statusConfig: Record<string, { label: string; bgColor: string; textColor: string }> = {
  ACTIVE: { label: 'Actif', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  INACTIVE: { label: 'Inactif', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  SUSPENDED: { label: 'Suspendu', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  TRIAL: { label: 'Essai', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  PENDING_VERIFICATION: { label: 'En attente', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
};

const solutionLabels: Record<string, string> = {
  academic: 'Gestion Academique',
  finance: 'Gestion Financiere',
  marketing: 'Gestion Marketing',
};

type TabType = 'overview' | 'users' | 'campuses' | 'academic';

export const ViewInstitutionModal: React.FC<ViewInstitutionModalProps> = ({
  isOpen,
  onClose,
  institutionId,
}) => {
  const [details, setDetails] = useState<InstitutionDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  useEffect(() => {
    if (isOpen && institutionId) {
      fetchDetails();
    }
  }, [isOpen, institutionId]);

  const fetchDetails = async () => {
    if (!institutionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('nexedu_access_token');
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/institutions/${institutionId}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des details');
      }

      const data = await response.json();
      setDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setDetails(null);
    setActiveTab('overview');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Apercu', icon: Building2 },
    { id: 'users', label: 'Utilisateurs', icon: Users },
    { id: 'campuses', label: 'Campus', icon: MapPin },
    { id: 'academic', label: 'Annees Academiques', icon: Calendar },
  ];

  const parseSolutions = (plan: string | null): string[] => {
    if (!plan) return [];
    return plan.split(',').map(s => s.trim().toLowerCase());
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-primary-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {details?.institution.name || 'Chargement...'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Details de l'etablissement
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            {details && (
              <div className="flex gap-1 mt-4 -mb-4 overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as TabType)}
                      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'bg-gray-100 text-primary-600 border-b-2 border-primary-600'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-primary-600" />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle size={20} />
                <p>{error}</p>
              </div>
            )}

            {details && !isLoading && (
              <>
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Institution Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Building2 size={18} className="text-gray-400" />
                          Informations de l'etablissement
                        </h3>

                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Nom</span>
                            <span className="font-medium">{details.institution.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Slug</span>
                            <span className="font-mono text-sm">{details.institution.slug}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Statut</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              statusConfig[details.institution.status]?.bgColor || 'bg-gray-100'
                            } ${statusConfig[details.institution.status]?.textColor || 'text-gray-700'}`}>
                              {statusConfig[details.institution.status]?.label || details.institution.status}
                            </span>
                          </div>
                          {details.institution.city && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Ville</span>
                              <span>{details.institution.city}</span>
                            </div>
                          )}
                          {details.institution.email && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Email</span>
                              <span>{details.institution.email}</span>
                            </div>
                          )}
                          {details.institution.phone && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Telephone</span>
                              <span>{details.institution.phone}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">Cree le</span>
                            <span>{new Date(details.institution.createdAt).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      </div>

                      {/* DG Info */}
                      <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <User size={18} className="text-gray-400" />
                          Directeur General
                        </h3>

                        {details.dgInfo ? (
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Nom complet</span>
                              <span className="font-medium">
                                {details.dgInfo.firstName} {details.dgInfo.lastName}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Email</span>
                              <div className="flex items-center gap-2">
                                <span>{details.dgInfo.email}</span>
                                {details.dgInfo.emailVerified ? (
                                  <CheckCircle size={14} className="text-green-500" />
                                ) : (
                                  <Clock size={14} className="text-amber-500" />
                                )}
                              </div>
                            </div>
                            {details.dgInfo.phone && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Telephone</span>
                                <span>{details.dgInfo.phone}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Statut</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                details.dgInfo.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {details.dgInfo.status === 'ACTIVE' ? 'Actif' : details.dgInfo.status}
                              </span>
                            </div>
                            {details.dgInfo.lastLoginAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Derniere connexion</span>
                                <span>{new Date(details.dgInfo.lastLoginAt).toLocaleString('fr-FR')}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                            Aucun DG assigne
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Solutions */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <BookOpen size={18} className="text-gray-400" />
                        Solutions Actives
                      </h3>

                      <div className="flex flex-wrap gap-2">
                        {parseSolutions(details.institution.subscriptionPlan).map((solution) => (
                          <span
                            key={solution}
                            className="px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
                          >
                            {solutionLabels[solution] || solution}
                          </span>
                        ))}
                        {parseSolutions(details.institution.subscriptionPlan).length === 0 && (
                          <span className="text-gray-500">Aucune solution active</span>
                        )}
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-blue-600 mb-1">
                          <Users size={16} />
                          <span className="text-sm font-medium">Utilisateurs</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{details.userStats.total}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-600 mb-1">
                          <MapPin size={16} />
                          <span className="text-sm font-medium">Campus</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{details.campuses.length}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-purple-600 mb-1">
                          <GraduationCap size={16} />
                          <span className="text-sm font-medium">Etudiants</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                          {details.userStats.byRole.find(r => r.role === 'STUDENT')?.count || 0}
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-amber-600 mb-1">
                          <Calendar size={16} />
                          <span className="text-sm font-medium">Annees</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">{details.academicYears.length}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-900">
                        Repartition des utilisateurs
                      </h3>
                      <span className="text-sm text-gray-500">
                        Total: {details.userStats.total} utilisateurs
                      </span>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-semibold text-gray-900">
                              Role
                            </th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-gray-900">
                              Nombre
                            </th>
                            <th className="text-right px-4 py-3 text-sm font-semibold text-gray-900">
                              Pourcentage
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {details.userStats.byRole
                            .filter(r => r.count > 0)
                            .sort((a, b) => b.count - a.count)
                            .map((role) => (
                              <tr key={role.role} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {role.label}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                  {role.count}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                  {details.userStats.total > 0
                                    ? `${((role.count / details.userStats.total) * 100).toFixed(1)}%`
                                    : '0%'}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Users with 0 count */}
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">
                        Roles sans utilisateurs:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {details.userStats.byRole
                          .filter(r => r.count === 0)
                          .map((role) => (
                            <span
                              key={role.role}
                              className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs"
                            >
                              {role.label}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Campuses Tab */}
                {activeTab === 'campuses' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      Campus ({details.campuses.length})
                    </h3>

                    {details.campuses.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <MapPin size={48} className="mx-auto text-gray-300 mb-2" />
                        <p>Aucun campus configure</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {details.campuses.map((campus) => (
                          <div
                            key={campus.id}
                            className={`border rounded-lg p-4 ${
                              campus.isActive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{campus.name}</h4>
                                <p className="text-sm text-gray-500">{campus.city || 'Ville non specifiee'}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                campus.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {campus.isActive ? 'Actif' : 'Inactif'}
                              </span>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                              <Users size={14} />
                              <span>{campus.userCount} utilisateurs</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Academic Years Tab */}
                {activeTab === 'academic' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      Annees Academiques ({details.academicYears.length})
                    </h3>

                    {details.academicYears.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar size={48} className="mx-auto text-gray-300 mb-2" />
                        <p>Aucune annee academique configuree</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {details.academicYears.map((year) => (
                          <div
                            key={year.id}
                            className={`border rounded-lg p-4 ${
                              year.isCurrent ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                  year.isCurrent ? 'bg-primary-100' : 'bg-gray-100'
                                }`}>
                                  <Calendar size={18} className={
                                    year.isCurrent ? 'text-primary-600' : 'text-gray-600'
                                  } />
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">{year.name}</h4>
                                  <p className="text-sm text-gray-500">
                                    {new Date(year.startDate).toLocaleDateString('fr-FR')} - {new Date(year.endDate).toLocaleDateString('fr-FR')}
                                  </p>
                                </div>
                              </div>
                              {year.isCurrent && (
                                <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                                  Annee courante
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
